import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase } from '../supabase';
import theme from '../theme';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function CardForm({ session, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    const { error: submitError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (submitError) {
      setError(submitError.message);
      setSubmitting(false);
      return;
    }

    // Recupera il payment method appena confermato e i dettagli della carta
    const paymentMethodId = setupIntent.payment_method;

    const { data: pmData } = await supabase.functions.invoke('save-payment-method', {
      body: { user_id: session.user.id, payment_method_id: paymentMethodId },
    });

    setSubmitting(false);

    if (pmData?.error) {
      setError(pmData.error);
      return;
    }

    onSuccess(pmData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && (
        <div style={{ color: theme.colors.error, fontSize: theme.font.size.sm, marginTop: theme.spacing.sm }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: theme.spacing.sm, marginTop: theme.spacing.md }}>
        <button type="button" onClick={onCancel} disabled={submitting}
          style={{ flex: 1, background: theme.colors.white, color: theme.colors.textMuted, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm, padding: 12, fontSize: theme.font.size.md, cursor: 'pointer' }}>
          Annulla
        </button>
        <button type="submit" disabled={!stripe || submitting}
          style={{ flex: 2, background: submitting ? '#ccc' : theme.colors.primary, color: theme.colors.white, border: 'none', borderRadius: theme.radius.sm, padding: 12, fontSize: theme.font.size.md, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer' }}>
          {submitting ? 'Salvataggio...' : '💳 Salva metodo di pagamento'}
        </button>
      </div>
    </form>
  );
}

function PaymentMethodCard({ session }) {
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [creatingIntent, setCreatingIntent] = useState(false);

  useEffect(() => { fetchPaymentMethod(); }, []);

  const fetchPaymentMethod = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('stripe_customers')
      .select('payment_method_validated, card_brand, card_last4')
      .eq('user_id', session.user.id)
      .maybeSingle();
    setPaymentMethod(data);
    setLoading(false);
  };

  const handleShowForm = async () => {
    setCreatingIntent(true);
    const { data, error } = await supabase.functions.invoke('create-setup-intent', {
      body: { user_id: session.user.id },
    });
    setCreatingIntent(false);
    if (error || !data?.client_secret) {
      alert('Errore nella preparazione del pagamento. Riprova.');
      return;
    }
    setClientSecret(data.client_secret);
    setShowForm(true);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setClientSecret(null);
    fetchPaymentMethod();
  };

  if (loading) return <p style={{ color: theme.colors.textMuted, fontSize: theme.font.size.base }}>Caricamento...</p>;

  if (showForm && clientSecret) {
    return (
      <Elements stripe={stripePromise} options={{ clientSecret, locale: 'it' }}>
        <CardForm session={session} onSuccess={handleSuccess} onCancel={() => setShowForm(false)} />
      </Elements>
    );
  }

  if (paymentMethod?.payment_method_validated) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, padding: theme.spacing.md, background: theme.colors.primaryLight, borderRadius: theme.radius.sm, marginBottom: theme.spacing.md }}>
          <span style={{ fontSize: 22 }}>💳</span>
          <div>
            <div style={{ fontWeight: 600, color: theme.colors.primary }}>
              {paymentMethod.card_brand?.toUpperCase()} •••• {paymentMethod.card_last4}
            </div>
            <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted }}>Metodo di pagamento attivo</div>
          </div>
        </div>
        <button onClick={handleShowForm} disabled={creatingIntent}
          style={{ background: 'none', border: `1px solid ${theme.colors.primary}`, color: theme.colors.primary, borderRadius: theme.radius.sm, padding: '8px 14px', fontSize: theme.font.size.base, cursor: 'pointer' }}>
          {creatingIntent ? 'Attendere...' : '🔄 Cambia metodo di pagamento'}
        </button>
      </div>
    );
  }

  return (
    <div>
      <p style={{ color: theme.colors.textMuted, fontSize: theme.font.size.base, marginBottom: theme.spacing.md }}>
        Nessun metodo di pagamento salvato. Aggiungine uno per poter accettare offerte a pagamento.
      </p>
      <button onClick={handleShowForm} disabled={creatingIntent}
        style={{ width: '100%', background: theme.colors.primary, color: theme.colors.white, border: 'none', borderRadius: theme.radius.sm, padding: 12, fontSize: theme.font.size.md, fontWeight: 700, cursor: 'pointer' }}>
        {creatingIntent ? 'Attendere...' : '➕ Aggiungi metodo di pagamento'}
      </button>
    </div>
  );
}

export default PaymentMethodCard;